import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { roundRate } from "@/lib/utils";
import { getRateAtDateQueryOptions } from "@/services/queries/exchange-rates";

interface UseConversionRateParams {
  /** Currency to convert FROM (e.g., transaction currency, invoice currency). */
  fromCurrencyId: string | undefined;
  /** Currency to convert TO (e.g., account currency, vessel currency). */
  toCurrencyId: string | undefined;
  /** Date string (YYYY-MM-DD) at which the rate applies. */
  date: string | undefined;
  /**
   * Saved rate from `defaultValues` (edit mode). Applied as the initial
   * override exactly once, the first time both `initialRate` is non-null and
   * the (from, to, date) context is complete. Leave undefined for create mode.
   */
  initialRate?: number;
  /**
   * Set to false to make the hook inert (e.g., when the rate field lives
   * inside an optional/toggled section). Default true.
   */
  enabled?: boolean;
}

interface UseConversionRateResult {
  /** Effective rate to display. undefined when not required, still loading, or explicitly cleared. */
  rate: number | undefined;
  /** Setter for user-entered values (also used to clear via `undefined`). */
  setRate: (rate: number | undefined) => void;
  /** True while the auto-fetch for the current context is in flight (and no override is set). */
  isLoading: boolean;
  /** True when the currency pair requires conversion at the given date. */
  isRequired: boolean;
}

/**
 * Returns the conversion rate for a (from, to, date) tuple, derived at render
 * time from React Query state + a user override. The displayed rate is a
 * function of the current context, not a form field synced via an effect, so
 * a context change updates the display in the **same paint** — no flash of
 * the prior saved value next to the new selection.
 *
 * **Override model**: the override is keyed by the current context. When the
 * context changes, the override is invalidated in render phase (synchronous)
 * and the display falls back to the auto-fetched query for the new context.
 * User-typed values stick across re-renders for the same context.
 *
 * **`initialRate` hydration**: applied as an override exactly once, when
 * (initialRate, context) first become valid together. The hydration window
 * closes on the first complete context — even one that doesn't need
 * conversion — so a saved matching-currency rate (`1.0`) never leaks into a
 * later differing-currency context.
 *
 * **Clearing semantics**: `setRate(undefined)` records an explicit clear,
 * leaving the display empty. The query is *not* re-triggered for the cleared
 * context. Changing any of (from, to, date) discards the override and
 * resumes auto-fetch.
 */
export function useConversionRate({
  fromCurrencyId,
  toCurrencyId,
  date,
  initialRate,
  enabled = true,
}: UseConversionRateParams): UseConversionRateResult {
  const isRequired =
    enabled &&
    !!fromCurrencyId &&
    !!toCurrencyId &&
    !!date &&
    fromCurrencyId !== toCurrencyId;
  const contextKey = `${fromCurrencyId ?? ""}|${toCurrencyId ?? ""}|${date ?? ""}`;

  // `isSet` distinguishes "no override yet (use auto-rate)" from "user cleared
  // (stay empty)" — both have undefined `value` but different downstream behavior.
  const [override, setOverride] = useState<{
    value: number | undefined;
    isSet: boolean;
  }>({ value: undefined, isSet: false });
  const [prevContextKey, setPrevContextKey] = useState(contextKey);

  // Render-phase reset: a context change invalidates the override synchronously
  // so the next render's display reflects the new context, not the prior saved
  // value. This is what eliminates the one-paint flicker the effect-based
  // implementation had.
  if (prevContextKey !== contextKey) {
    setPrevContextKey(contextKey);
    setOverride({ value: undefined, isSet: false });
  }

  // Close the hydration window on the first complete (from, to, date) tuple —
  // even when conversion isn't required — so a saved matching-currency
  // `initialRate` doesn't leak into a later differing-currency context. Apply
  // `initialRate` to the override only when conversion is actually needed at
  // that moment.
  const isComplete = !!fromCurrencyId && !!toCurrencyId && !!date;
  const [hasHydrated, setHasHydrated] = useState(false);
  if (!hasHydrated && isComplete && prevContextKey === contextKey) {
    setHasHydrated(true);
    if (isRequired && initialRate != null && !override.isSet) {
      setOverride({ value: initialRate, isSet: true });
    }
  }

  const query = useQuery({
    ...getRateAtDateQueryOptions(
      fromCurrencyId ?? "",
      toCurrencyId ?? "",
      date ?? ""
    ),
    enabled: isRequired && !override.isSet,
  });

  const autoRate =
    query.data?.value?.rate != null
      ? roundRate(query.data.value.rate)
      : undefined;
  // Honor an explicit user override even when the *current* context doesn't
  // require conversion (e.g. transfer-form's "accounts differ, currency not
  // picked yet" branch). The override is keyed by context, so it self-clears
  // on context change — preserving the user's input until the context moves
  // and then yielding to the new auto-fetch.
  const rate = override.isSet
    ? override.value
    : isRequired
      ? autoRate
      : undefined;

  const setRate = useCallback((newRate: number | undefined) => {
    setOverride({ value: newRate, isSet: true });
  }, []);

  return {
    rate,
    setRate,
    isLoading: isRequired && !override.isSet && query.isFetching,
    isRequired,
  };
}
